import React, { useContext, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { Sidebar } from './components/Sidebar';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { ClientManagement } from './pages/ClientManagement';
import SalesManagement from './pages/SalesManagement';
import PurchaseManagement from './pages/PurchaseManagement';
import ClientBySalesExecutiveList from './pages/ClientBySalesExecutiveList';
import ClientByPurchaseExecutiveList from './pages/ClientByPurchaseExecutiveList';
import CustomerSalesDetails from './pages/CustomerSalesDetail';
import VendorPurchaseDetails from './pages/VendorPurchaseDetails';
import ReturnsManagement from './pages/ReturnsManagement';
import PaymentManagement from './pages/PaymentManagement';
import { ClientTransactions } from './pages/ClientTransactions';
import VendorTransactions from './pages/VendorTransactionsTable';
import UserManagement from './pages/UserManagement';
import SalesExecutiveSummary from './pages/SalesExecutiveSummary';
import PurchaseExecutiveSummary from './pages/PurchaseExecutiveSummary';
import ExecutiveOfficersPage from './pages/ExecutiveOfficersPage';
import MajorClientReport from './pages/MajorClientReport';
import OverallSalesReport from './pages/OverallSalesReport';
import OverallPurchaseReport from './pages/OverallPurchaseReport';
import ItemManagement from './pages/ItemManagement';
import MonthWiseBalanceReport from './pages/MonthWiseBalanceReport';

import {
  getProducts,
  addProduct,
  deleteProduct,
  getRawMaterials,
  addRawMaterial,
  deleteRawMaterial,
  getBranches,
  addBranch,
  deleteBranch,
  getCompanyAccounts,
  addCompanyAccount,
  deleteCompanyAccount
} from './utils/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useContext(AuthContext);
  const role = user?.role?.toLowerCase();

  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/dashboard" />;
  }
  return children;
};

function AppContent() {
  const { token, user } = useContext(AuthContext);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  return (
    <Router>
      <div className={token ? "app-layout flex-column flex-md-row" : "login-layout"}>
        {token && (
          <div className="mobile-header d-flex justify-content-between align-items-center d-md-none px-3 py-2 bg-white border-bottom w-100" style={{ zIndex: 1005 }}>
            <h5 className="m-0 text-primary fw-bold"><i className="bi bi-gem me-2"></i>Omega Account</h5>
            <button className="btn p-1 border-0" onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}>
              <i className="bi bi-list fs-1 text-dark"></i>
            </button>
          </div>
        )}
        {token && <Sidebar mobileOpen={isMobileSidebarOpen} setMobileOpen={setIsMobileSidebarOpen} />}
        <div className={token ? "main-content" : "auth-content"}>
          <Routes>
            {/* Login Route: Redirect to dashboard if already logged in */}
            <Route path="/login" element={token ? <Navigate to="/dashboard" /> : <Login />} />

            <Route path="/dashboard" element={token ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/clients" element={token ? <ClientManagement /> : <Navigate to="/login" />} />
            <Route path="/client/:id/transactions" element={token ? <ClientTransactions /> : <Navigate to="/login" />} />
            <Route path="/vendors/transactions/:vendorId" element={token ? <VendorTransactions /> : <Navigate to="/login" />} />

            {/* ✅ Sales Management Routes */}
            <Route path="/sales" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'normal_user']}><SalesManagement /></ProtectedRoute>} />
            <Route path="/sales/clients-by-officer/:officerName" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'normal_user']}><ClientBySalesExecutiveList /></ProtectedRoute>} />
            <Route path="/sales-executive-summary/:officerName" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'normal_user']}><SalesExecutiveSummary /></ProtectedRoute>} />
            <Route path="/customer-sales/:clientId" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'normal_user']}><CustomerSalesDetails /></ProtectedRoute>} />
            <Route path="/major-client-report" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'normal_user']}><MajorClientReport /></ProtectedRoute>} />
            <Route path="/overall-sales" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'normal_user']}><OverallSalesReport /></ProtectedRoute>} />
            <Route path="/overall-purchases" element={<ProtectedRoute allowedRoles={['admin', 'purchase_manager', 'normal_user']}><OverallPurchaseReport /></ProtectedRoute>} />
            <Route path="/month-wise-balance-report" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'purchase_manager', 'normal_user']}><MonthWiseBalanceReport /></ProtectedRoute>} />

            {/* Purchase Management Routes */}
            <Route path="/purchases" element={<ProtectedRoute allowedRoles={['admin', 'purchase_manager', 'normal_user']}><PurchaseManagement /></ProtectedRoute>} />
            <Route path="/purchases/clients-by-officer/:officerName" element={<ProtectedRoute allowedRoles={['admin', 'purchase_manager', 'normal_user']}><ClientByPurchaseExecutiveList /></ProtectedRoute>} />
            <Route path="/purchase-executive-summary/:officerName" element={<ProtectedRoute allowedRoles={['admin', 'purchase_manager', 'normal_user']}><PurchaseExecutiveSummary /></ProtectedRoute>} />
            <Route path="/vendor-purchases/:vendorId" element={<ProtectedRoute allowedRoles={['admin', 'purchase_manager', 'normal_user']}><VendorPurchaseDetails /></ProtectedRoute>} />

            {/* Inventory Management Routes */}
            <Route path="/products" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'purchase_manager', 'normal_user']}><ItemManagement type="product" title="Product" fetchItems={getProducts} addItem={addProduct} deleteItem={deleteProduct} /></ProtectedRoute>} />
            <Route path="/raw-materials" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'purchase_manager', 'normal_user']}><ItemManagement type="raw-material" title="Raw Material" fetchItems={getRawMaterials} addItem={addRawMaterial} deleteItem={deleteRawMaterial} /></ProtectedRoute>} />
            <Route path="/branches" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'purchase_manager', 'normal_user']}><ItemManagement type="branch" title="Branch" fetchItems={getBranches} addItem={addBranch} deleteItem={deleteBranch} /></ProtectedRoute>} />
            <Route path="/company-accounts" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'purchase_manager', 'normal_user']}><ItemManagement type="company-account" title="Company Account" fetchItems={getCompanyAccounts} addItem={addCompanyAccount} deleteItem={deleteCompanyAccount} /></ProtectedRoute>} />

            {/* Returns Management Route */}
            <Route path="/returns" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'purchase_manager', 'normal_user']}><ReturnsManagement /></ProtectedRoute>} />

            {/* Payment Management Route */}
            <Route path="/payments" element={<ProtectedRoute allowedRoles={['admin']}><PaymentManagement /></ProtectedRoute>} />

            {/* ✅ User Management Route */}
            <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />

            {/* Executive Officers Route */}
            <Route path="/executive-officers" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'purchase_manager', 'normal_user']}><ExecutiveOfficersPage /></ProtectedRoute>} />

            <Route path="/" element={<Navigate to={token ? '/dashboard' : '/login'} />} />
            <Route path="*" element={<Navigate to={token ? '/dashboard' : '/login'} />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;